import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CalendarClock, Cake, CalendarCheck, MoreHorizontal, Plus, Users } from "lucide-react";
import { Button, Card, IconButton, TableAvatar, TablePill, TableStatus, type TableTone } from "@akuna/ui";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { StayPack } from "@/hooks/useStayPacks";
import { isStayPackActiveOnDate } from "../reservation/useReservationPacks";
import {
  formatArrival,
  formatStayRange,
  isBirthdayOnDate,
  parseIsoDate,
  paymentState,
  reservationsOnDate,
  resolveDisplayStatus,
  type ScheduleReservation,
  type StayShowFilter,
} from "./scheduleModel";
import { getScheduleStatusTone } from "./scheduleStatusTone";
import { rescheduleAvailableForStatus } from "./scheduleInteraction";

interface DayReservationsCardProps {
  isoDate: string;
  packs: StayPack[];
  reservations: ScheduleReservation[];
  show: StayShowFilter;
  onNewReservation: () => void;
  onReservationAction: (reservation: ScheduleReservation) => void;
  /** Abre a remarcação de uma diária específica. Sem isso a ação não aparece. */
  onReschedule?: (reservation: ScheduleReservation, dayId: string) => void;
}

const NATURE_TONE: Record<ScheduleReservation["nature"], TableTone> = {
  daycare: "orange",
  hotel: "purple",
  both: "blue",
};

const PAYMENT_TONE = {
  paid_pack: "green",
  paid_single: "green",
  plan: "neutral",
  due: "amber",
} as const satisfies Record<string, TableTone>;

/** Shared by the header row and every data row so the columns stay aligned. */
const COLUMNS = "md:grid md:grid-cols-[1.8fr_1.1fr_1.1fr_0.8fr_0.9fr_1.1fr_0.9fr_56px] md:gap-2.5";

export default function DayReservationsCard({
  isoDate,
  packs,
  reservations,
  show,
  onNewReservation,
  onReservationAction,
  onReschedule,
}: DayReservationsCardProps) {
  const { i18n, t } = useTranslation("daycareHotel");
  const locale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";
  const dayReservations = reservationsOnDate(reservations, isoDate, show);
  const dayPacks = packs.filter((pack) => isStayPackActiveOnDate(pack, isoDate));
  const dayLabel = parseIsoDate(isoDate).toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });

  return (
    <Card className="overflow-hidden p-0">
      <header className="flex items-center gap-2.5 border-b border-border px-5 py-4">
        <CalendarCheck aria-hidden="true" size={20} className="text-primary" />
        <h2 className="font-display text-base font-bold text-foreground">
          {t("schedule.dayListTitle", { date: dayLabel })}
        </h2>
      </header>

      {dayPacks.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border bg-muted px-5 py-3">
          <span className="inline-flex items-center gap-1.5 font-body text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground">
            <Users aria-hidden="true" size={16} className="text-neutral-400" />
            {t("schedule.packsOfTheDay")}
          </span>
          {dayPacks.map((pack) => (
            <span
              key={pack.id}
              className="inline-flex items-center gap-[7px] rounded-full border-[1.5px] border-border bg-secondary px-[13px] py-1.5 font-body text-[13px] font-semibold text-foreground"
            >
              <span
                className="h-[9px] w-[9px] shrink-0 rounded-full bg-brand-purple"
                style={pack.color ? { background: pack.color } : undefined}
              />
              {pack.name}
            </span>
          ))}
        </div>
      )}

      <div
        className={`hidden bg-muted px-5 py-[11px] font-body text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground ${COLUMNS}`}
      >
        <div>{t("schedule.columnPet")}</div>
        <div>{t("schedule.columnNature")}</div>
        <div>{t("schedule.columnStay")}</div>
        <div>{t("schedule.columnArrival")}</div>
        <div>{t("schedule.columnStatus")}</div>
        <div>{t("schedule.columnPayment")}</div>
        <div>{t("schedule.columnBirthday")}</div>
        <div className="text-center">{t("schedule.columnActions")}</div>
      </div>

      {dayReservations.length === 0 ? (
        <div className="flex flex-col items-center gap-4 px-6 py-14 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <CalendarCheck size={26} className="text-primary" />
          </div>
          <div className="space-y-1">
            <p className="font-display text-[15px] font-semibold text-foreground">{t("schedule.dayEmptyTitle")}</p>
            <p className="text-sm text-muted-foreground">{t("schedule.dayEmptyDescription")}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onNewReservation}>
            <Plus size={16} /> {t("newReservation")}
          </Button>
        </div>
      ) : (
        <ul>
          {dayReservations.map((reservation) => {
            const stayRange = formatStayRange(reservation);
            const arrival = formatArrival(reservation.checkInTime);
            const payment = paymentState(reservation);
            const displayStatus = resolveDisplayStatus(reservation, isoDate);
            const isBirthday = isBirthdayOnDate(reservation.birthDate, isoDate);

            return (
              <li key={reservation.id}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onReservationAction(reservation)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onReservationAction(reservation);
                    }
                  }}
                  className={`w-full cursor-pointer items-center border-t border-border px-5 py-3 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring max-md:flex max-md:flex-wrap max-md:gap-2 ${COLUMNS}`}
                >
                  <div className="flex items-center gap-2.5">
                    <TableAvatar name={reservation.petName} tone={NATURE_TONE[reservation.nature]} />
                    <div className="min-w-0">
                      <Link
                        to={`/patients/pets/${reservation.petId}`}
                        onClick={(event) => event.stopPropagation()}
                        className="block truncate font-display text-sm font-bold leading-tight text-primary hover:underline"
                      >
                        {reservation.petName}
                      </Link>
                      {reservation.ownerId ? (
                        <Link
                          to={`/patients/owners/${reservation.ownerId}`}
                          onClick={(event) => event.stopPropagation()}
                          className="block truncate font-body text-xs text-muted-foreground hover:underline"
                        >
                          {reservation.ownerName}
                        </Link>
                      ) : (
                        <div className="truncate font-body text-xs text-muted-foreground">{reservation.ownerName}</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <TablePill tone={NATURE_TONE[reservation.nature]}>
                      {t(`schedule.nature.${reservation.nature}`)}
                    </TablePill>
                  </div>

                  <div className="font-body text-[13px] text-neutral-600">
                    {stayRange ?? (reservation.bedLabel || "—")}
                  </div>

                  <div className="font-body text-[13px] text-muted-foreground">
                    {reservation.arrivalShift ? (
                      <span className="block font-semibold text-foreground">
                        {t(reservation.arrivalShift === "morning" ? "shiftMorning" : "shiftEvening")}
                      </span>
                    ) : null}
                    {arrival ?? (reservation.arrivalShift ? null : "—")}
                  </div>

                  <div>
                    <TableStatus
                      tone={getScheduleStatusTone(displayStatus)}
                      label={t(`schedule.status.${displayStatus}`)}
                    />
                  </div>

                  <div>
                    <TablePill tone={PAYMENT_TONE[payment]}>{t(`schedule.payment.${payment}`)}</TablePill>
                  </div>

                  <div className="flex items-center gap-2">
                    {isBirthday && (
                      <TablePill tone="pink">
                        <Cake aria-hidden="true" size={12} className="mr-1 inline-block" />
                        {t("badgeBirthday")}
                      </TablePill>
                    )}
                  </div>

                  {/* A agenda do dia só tem uma ação por diária; o menu deixa claro o que ela faz. */}
                  <div className="flex items-center justify-end md:justify-center max-md:ms-auto">
                    {onReschedule && rescheduleAvailableForStatus(displayStatus) && reservation.dayIdByDate?.[isoDate] ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(event) => event.stopPropagation()}>
                          <IconButton
                            label={t("schedule.actionsMenu", { pet: reservation.petName })}
                            variant="outline"
                            className="!h-8 !w-8 rounded-md"
                          >
                            <MoreHorizontal aria-hidden="true" className="size-4" />
                          </IconButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60 p-1.5" onClick={(event) => event.stopPropagation()}>
                          <DropdownMenuItem
                            onSelect={() => onReschedule(reservation, reservation.dayIdByDate![isoDate])}
                            className="flex items-start gap-2"
                          >
                            <CalendarClock aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                            <span className="grid">
                              <span>{t("reschedule.action")}</span>
                              <span className="text-xs text-muted-foreground">{t("reschedule.actionHint")}</span>
                            </span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
